import Decimal from "decimal.js";
import _ from "lodash";

import { HttpAction } from "@megaron/http-service";
import { PubsubTopic } from "@megaron/pubsub-contracts";
import { Failure, Ok } from "@megaron/result";
import { Serializer, Serializers, SerializerValue } from "@megaron/serializers";
import { uuidSerializer } from "@megaron/uuid";

export type SizeUnit = string;

export const defaultItemVariantOptionName = "Title";

export const itemVariantOptionSerializer = Serializers.object({
  optionName: Serializers.string,
  name: Serializers.string,
});
export type ItemVariantOption = SerializerValue<typeof itemVariantOptionSerializer>;
export type ItemVariant = ItemVariantOption[];

export const formatItemVariant = (variant: readonly ItemVariantOption[] | null | undefined): string =>
  (variant ?? []).map((option) => `${option.optionName}: ${option.name}`).join(" / ");

export const parseItemVariantTitle = (title: string): ItemVariant => {
  const trimmedTitle = title.trim();
  if (!trimmedTitle || trimmedTitle === "Default Title") return [];

  return trimmedTitle
    .split(/\s+\/\s+/)
    .map((part) => {
      const separatorIndex = part.indexOf(":");
      if (separatorIndex < 0) return { optionName: defaultItemVariantOptionName, name: part.trim() };

      const optionName = part.slice(0, separatorIndex).trim();
      const name = part.slice(separatorIndex + 1).trim();

      if (!optionName || !name) return { optionName: defaultItemVariantOptionName, name: part.trim() };
      return { optionName, name };
    })
    .filter((option) => option.name);
};

const itemVariantTitleSerializer: Serializer<ItemVariant> = {
  serialize: formatItemVariant,
  deserialize: (raw) => {
    if (typeof raw !== "string") return Failure("NotAString");
    return Ok(parseItemVariantTitle(raw));
  },
};

export const itemVariantSerializer = Serializers.first(itemVariantOptionSerializer.array(), itemVariantTitleSerializer);

export const normalizeItemVariant = (raw: unknown): ItemVariant => {
  const result = itemVariantSerializer.deserialize(raw);
  return result.isOk ? result.value : [];
};

export const itemSerializer = Serializers.object({
  itemId: Serializers.string,
  productId: Serializers.string,
  itemName: Serializers.string,
  gtin: Serializers.string.nullable({ emptyStrAsNull: true }),
  box: Serializers.integer.nullable({ emptyStrAsNull: true }),
  layer: Serializers.integer.nullable({ emptyStrAsNull: true }),
  pallet: Serializers.integer.nullable({ emptyStrAsNull: true }),
  size: Serializers.decimal,
  sizeUnit: Serializers.string,
  massKg: Serializers.decimal,
  bcId: uuidSerializer.nullable({ emptyStrAsNull: true }),
  productGroup: Serializers.string,
  title: Serializers.string,
  variant: itemVariantSerializer,
  description: Serializers.string,
  brand: Serializers.string,
  handle: Serializers.string.nullable({ emptyStrAsNull: true }),
  shopifyProductId: Serializers.string.nullable({ emptyStrAsNull: true }),
  shopifyVariantId: Serializers.string.nullable({ emptyStrAsNull: true }),
  cost: Serializers.decimal.nullable({ emptyStrAsNull: true }),
  isArchived: Serializers.boolean,
  isLoyalty: Serializers.boolean,
  defaultProfit: Serializers.decimal.nullable({ emptyStrAsNull: true }),
  isInAvailability: Serializers.boolean,
});

export type Item = SerializerValue<typeof itemSerializer>;
export const newItem = (input?: Partial<Item>): Item => ({
  itemId: "",
  productId: "",
  itemName: "",
  gtin: null,
  box: null,
  layer: null,
  pallet: null,
  size: new Decimal(0),
  massKg: new Decimal(0),
  bcId: null,
  productGroup: "",
  title: "",
  variant: [],
  description: "",
  brand: "",
  cost: null,
  isArchived: false,
  isLoyalty: false,
  defaultProfit: null,
  isInAvailability: false,
  handle: null,
  shopifyVariantId: null,
  shopifyProductId: null,
  sizeUnit: "kg",
  ..._.pickBy(input, (v) => v !== undefined),
});

export const itemsActions = {
  itemsQuery: HttpAction({
    path: "/items",
    method: "get",
    qsSerializer: Serializers.object({
      itemIds: Serializers.string.array().asJsonString(),
    }).partial(),
    requiresAuth: true as const,
    valueSerializer: Serializers.object({ items: itemSerializer.array(), count: Serializers.integer }),
  }),

  itemQuery: HttpAction({
    path: "/item",
    method: "get",
    qsSerializer: Serializers.first(
      Serializers.object({ itemId: Serializers.string }),
      Serializers.object({ gtin: Serializers.string }),
    ),
    requiresAuth: true as const,
    valueSerializer: itemSerializer,
  }),

  saveItem: HttpAction({
    path: "/items",
    method: "put",
    requiresAuth: true as const,
    bodySerializer: itemSerializer,
    valueSerializer: itemSerializer,
  }),

  saveItemCosts: HttpAction({
    path: "/items/costs",
    method: "put",
    requiresAuth: true as const,
    bodySerializer: Serializers.object({
      itemId: Serializers.string,
      cost: Serializers.decimal.nullable(),
    }).array(),
    valueSerializer: itemSerializer.array(),
  }),
};

export const itemChangedTopic = PubsubTopic({
  name: "crm-item-changed",
  serializer: Serializers.object({
    itemId: Serializers.string,
  }),
});

export type ItemChangedMessage = SerializerValue<typeof itemChangedTopic.serializer>;
